import { useEffect, useRef, useState } from 'react';

/**
 * Observes the aisle scroll positions and updates the active aisle ID.
 *
 * @param {Array} aisles - The list of aisles to observe
 * @returns {Object} - Hook return object
 * @returns {number} activeAisleID - The ID of the active aisle
 * @returns {Object} aisleRefs - The reference to the aisle elements (used to observe the scroll positions)
 */
export function useActiveAisleObserver(aisles, disabled) {
    const [activeAisleID, setActiveAisleID] = useState(null);
    const aisleRefs = useRef({});

    useEffect(() => {
        // ESLint requires an explicit return value if there are other return paths
        if (disabled) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

                if (visible.length > 0) {
                    setActiveAisleID(Number(visible[0].target.dataset.aisleId));
                }
            },
            {
                rootMargin: '-15% 0px -75% 0px',
                threshold: 0.05,
            },
        );

        Object.values(aisleRefs.current).forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => observer.disconnect();
    }, [aisles, disabled]);

    return { activeAisleID, aisleRefs };
}
